import React, { useEffect } from "react";

// region Libraries
import { useTranslation } from "react-i18next";
import { Close } from "@libraries/mui/icons";
// endregion Libraries
// region Atoms
import ModuleHex from "@atoms/ModuleHex";
// endregion Atoms
// region Languages
import { GlobalMessages, LauncherMessages } from "@languages/interfaces";
// endregion Languages
// region Types
import { Module } from "@pages/Launcher/types";
// endregion Types
// region Styled components
import * as Styled from "./styles";
// endregion Styled components

export interface ModuleUpsellModalProps {
  /** Module to present — `null` keeps the modal closed. */
  module: Module | null;
  onClose: () => void;
}

/**
 * Informational notice shown when a `locked-not-contracted` card is clicked.
 * Carries no call to action: the message tells the user the module is not
 * contracted and invites them to reach out to their consultant. Closing the
 * modal is the only path forward.
 */
const ModuleUpsellModal: React.FC<ModuleUpsellModalProps> = ({ module, onClose }) => {

  const { t } = useTranslation();

  useEffect(() => {
    if (!module) return undefined;

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };

    window.addEventListener("keydown", handleEscape);

    return () => window.removeEventListener("keydown", handleEscape);
  }, [module, onClose]);

  const handleOverlayClick = (event: React.MouseEvent<HTMLDivElement>) => {
    if (event.target === event.currentTarget) onClose();
  };

  if (!module) return null;

  return (
    <Styled.Overlay onClick={handleOverlayClick}>
      <Styled.Modal role="dialog" aria-modal="true" aria-label={module.label}>
        <Styled.Header>
          <Styled.HexBadge>
            <ModuleHex accent={module.accent} size={52} />
          </Styled.HexBadge>
          <Styled.HeaderText>
            <Styled.Title>{module.label}</Styled.Title>
          </Styled.HeaderText>
          <Styled.CloseButton type="button" onClick={onClose} aria-label={t(GlobalMessages.buttonCancel)}>
            <Close fontSize="small" />
          </Styled.CloseButton>
        </Styled.Header>

        <Styled.Body>
          <Styled.Note $accent={module.accent}>
            <p>{t(LauncherMessages.launcherUpsellNote)}</p>
          </Styled.Note>
        </Styled.Body>
      </Styled.Modal>
    </Styled.Overlay>
  );
};

export default React.memo(ModuleUpsellModal);
